import { useNavigate } from "react-router-dom";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";

const TIERS = [
  {
    name: "Free",
    price: "$0",
    description: "For casual travelers exploring options.",
    features: [
      "Unlimited visa checks",
      "AI travel assistant",
      "Basic document checklist",
      "Save up to 3 trips",
    ],
    cta: "Current plan",
    highlight: false,
  },
  {
    name: "Premium",
    price: "$9",
    period: "/month",
    description: "For serious travelers who need certainty.",
    features: [
      "Everything in Free",
      "In-depth premium PDF reports",
      "Step-by-step application guide",
      "Photo & biometric specs",
      "Rejection risk analysis",
      "Priority AI assistant",
    ],
    cta: "Claim premium (demo)",
    highlight: true,
  },
];

export default function Pricing() {
  const { user, claimPremium } = useAuth();
  const navigate = useNavigate();

  const upgrade = async () => {
    if (!user) {
      navigate("/register");
      return;
    }
    try {
      await claimPremium();
      toast.success("Premium activated!");
    } catch {
      toast.error("Could not activate premium");
    }
  };

  return (
    <div className="min-h-screen bg-grid">
      <Navbar />
      <main className="mx-auto max-w-5xl px-4 py-12 sm:px-6 lg:py-20">
        <div className="text-center">
          <h1 className="font-heading text-4xl font-bold sm:text-5xl">Simple, honest pricing</h1>
          <p className="mx-auto mt-3 max-w-xl text-slate-600">
            Payment integration coming soon. For now, claim premium instantly to explore all
            features.
          </p>
        </div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {TIERS.map((tier) => (
            <div
              key={tier.name}
              data-testid={`tier-${tier.name.toLowerCase()}`}
              className={`relative rounded-3xl border p-8 ${
                tier.highlight
                  ? "border-slate-900 bg-slate-900 text-white shadow-2xl"
                  : "border-slate-200 bg-white"
              }`}
            >
              {tier.highlight && (
                <div className="absolute -top-3 left-8 inline-flex items-center gap-1 rounded-full bg-amber-500 px-3 py-1 text-xs font-semibold text-white">
                  <Sparkles className="h-3 w-3" /> Most popular
                </div>
              )}
              <h3 className="font-heading text-2xl font-bold">{tier.name}</h3>
              <p className={`mt-1 text-sm ${tier.highlight ? "text-slate-300" : "text-slate-500"}`}>
                {tier.description}
              </p>
              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-heading text-5xl font-extrabold">{tier.price}</span>
                {tier.period && (
                  <span className={tier.highlight ? "text-slate-400" : "text-slate-500"}>
                    {tier.period}
                  </span>
                )}
              </div>
              <ul className="mt-6 space-y-3">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check
                      className={`mt-0.5 h-4 w-4 shrink-0 ${
                        tier.highlight ? "text-teal-400" : "text-teal-600"
                      }`}
                    />
                    {f}
                  </li>
                ))}
              </ul>

              {tier.highlight ? (
                <Button
                  onClick={upgrade}
                  disabled={user?.is_premium}
                  data-testid="upgrade-btn"
                  className="mt-8 h-12 w-full bg-white text-base font-semibold text-slate-900 hover:bg-slate-100 disabled:opacity-70"
                >
                  {user?.is_premium ? "You're on Premium" : tier.cta}
                </Button>
              ) : (
                <Button
                  disabled
                  variant="outline"
                  className="mt-8 h-12 w-full text-base"
                >
                  {tier.cta}
                </Button>
              )}
            </div>
          ))}
        </div>

        <p className="mt-10 text-center text-xs text-slate-400">
          Payment processing is deferred in this demo. Premium can be claimed at no cost.
        </p>
      </main>
    </div>
  );
}
